import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, User, Lock, Save, Users, Database } from 'lucide-react';
import api from '../utils/api';
import UserManagement from '../components/UserManagement';
import DatabaseAdmin from '../components/DatabaseAdmin';

const Settings = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  
  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || '',
    company_name: user?.company_name || '',
    phone: user?.phone || ''
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handleProfileChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.patch('/auth/profile', profileData);
      toast.success('Profile updated successfully!');
      // Reload user data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const dashboardLink = (user?.role === 'admin' || user?.role === 'super_admin') ? '/admin/dashboard' : '/dashboard';
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  return (
    <div className="app-layout min-h-screen">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link
            to={dashboardLink}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Dashboard
          </Link>
          <div className="text-xl font-bold text-slate-900">
            <span className="gradient-text">Settings</span>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="settings-title">Account Settings</h1>
          <p className="text-slate-600">Manage your profile and security settings</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-slate-200">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab('profile')}
              className={`pb-3 px-2 border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'profile'
                  ? 'border-purple-600 text-purple-600 font-semibold'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
              data-testid="tab-profile"
            >
              <User size={20} />
              Profile
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`pb-3 px-2 border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'password'
                  ? 'border-purple-600 text-purple-600 font-semibold'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
              data-testid="tab-password"
            >
              <Lock size={20} />
              Password
            </button>
            {isAdmin && (
              <button
                onClick={() => setActiveTab('users')}
                className={`pb-3 px-2 border-b-2 transition-colors flex items-center gap-2 ${
                  activeTab === 'users'
                    ? 'border-purple-600 text-purple-600 font-semibold'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
                data-testid="tab-users"
              >
                <Users size={20} />
                User Management
              </button>
            )}
            {isSuperAdmin && (
              <button
                onClick={() => setActiveTab('database')}
                className={`pb-3 px-2 border-b-2 transition-colors flex items-center gap-2 ${
                  activeTab === 'database'
                    ? 'border-purple-600 text-purple-600 font-semibold'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
                data-testid="tab-database"
              >
                <Database size={20} />
                Database Admin
              </button>
            )}
          </div>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="dashboard-card">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Profile Information</h2>
            <form onSubmit={handleProfileSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  className="w-full px-4 py-3 bg-slate-100 border border-slate-200 rounded-lg text-slate-500 cursor-not-allowed"
                  disabled
                />
                <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  value={profileData.full_name}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  data-testid="full-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Company Name</label>
                <input
                  type="text"
                  name="company_name"
                  value={profileData.company_name}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  data-testid="company-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  placeholder="+1 (555) 000-0000"
                  data-testid="phone-input"
                />
              </div>

              <div className="flex items-center gap-2 pt-4 border-t border-slate-200">
                <div className="text-sm text-slate-600">
                  <strong>Role:</strong> {user?.role === 'super_admin' ? 'Super Administrator' : user?.role === 'admin' ? 'Administrator' : 'Client'}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="primary-button flex items-center gap-2 disabled:opacity-50"
                data-testid="save-profile-btn"
              >
                <Save size={20} />
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <div className="dashboard-card">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Change Password</h2>
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Current Password</label>
                <input
                  type="password"
                  name="current_password"
                  value={passwordData.current_password}
                  onChange={handlePasswordChange}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  required
                  data-testid="current-password-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">New Password</label>
                <input
                  type="password"
                  name="new_password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
                <p className="text-xs text-slate-500 mt-1">Must be at least 6 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Confirm New Password</label>
                <input
                  type="password"
                  name="confirm_password"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  required
                  minLength={6}
                  data-testid="confirm-password-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="primary-button flex items-center gap-2 disabled:opacity-50"
                data-testid="change-password-btn"
              >
                <Lock size={20} />
                {loading ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>
        )}

        {/* User Management Tab (All Admins) */}
        {activeTab === 'users' && isAdmin && (
          <div className="dashboard-card">
            <UserManagement canDelete={isSuperAdmin} />
          </div>
        )}

        {/* Database Admin Tab (Super Admin Only) */}
        {activeTab === 'database' && isSuperAdmin && (
          <div className="dashboard-card">
            <DatabaseAdmin />
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
